import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import useErrorModal from "./useErrorModal";
import { useDispatch, useSelector } from "react-redux";
import { SEND_MONEY_TRANSFER_METHOD_IDS } from "../constants/enums";
import { ROUTES } from "../constants/clientRoutes";
import { ROUTES as API_ROUTES } from "../constants/api";
import { MOBILE_WALLET_LINK_ID, MY_CARD_TRANSFER_LINKS } from "../constants/myCardLinks";
import { GetApiData } from "../api/general";
import { FEATURE_FLAGS } from "../constants/featureFlags";
import { SetSelectedRecipient, SetSendMoneyCountryCurrency, SetSendMoneyLimits, SetSendMoneyTransferMethod } from "../actions/sendMoney";

// types
import { SendMoneyRecipientsResponse } from "../types/api/sendMoney";
import { NavigationPanelListItemType } from "../types/components";
import { RootState } from "../types/redux";
import { APIModalType } from "../types/api/card";

export const useMyCardTransferLinks = (): { links: NavigationPanelListItemType[]; loading: boolean; restrictions: APIModalType | null } => {
  const history = useHistory();
  const dispatch = useDispatch();
  const openErrorModal = useErrorModal();
  const featureFlags = useSelector((state: RootState) => state.user.featureFlags);
  const [restrictions, setRestrictions] = useState<APIModalType | null>(null);
  const [loading, setLoading] = useState(false);

  const onMobileWalletClick = useCallback(async () => {
    setLoading(true);
    try {
      const url = API_ROUTES.SEND_MONEY.GET_SEND_MONEY_ACCOUNTS(
        SEND_MONEY_TRANSFER_METHOD_IDS.MOBILE_WALLET
      );
      const response = await GetApiData<SendMoneyRecipientsResponse>(url);

      const result = response?.result;

      if (!result) {
        // no result — treat as failure. We're already on MyCard, so no
        // navigation/unmount happens; clear loading or the spinner sticks.
        setLoading(false);
        history.push(ROUTES.MY_CARD.INDEX);
        return;
      }

      // if there are restrictions, show the modal and don't navigate
      if (result.restrictions && Object.keys(result.restrictions).length) {
        setLoading(false);
        setRestrictions(result.restrictions);
        return;
      }

      const accounts = result?.accounts ?? [];

      dispatch(SetSendMoneyLimits({
        limitDisplays: result?.limitDisplays ?? [],
        limits: result.limits ?? null,
        walletLimitsApply: result?.walletLimitsApply ?? false,
      }));

      dispatch(SetSendMoneyTransferMethod(SEND_MONEY_TRANSFER_METHOD_IDS.MOBILE_WALLET));

      // Entering the flow starts fresh: clears the selected recipient, country/currency
      // (and sessionStorage), plus the operator and quote that depend on them.
      dispatch(SetSelectedRecipient(null));
      dispatch(SetSendMoneyCountryCurrency({ country: "", currency: "" }));

      accounts.length
        ? history.push(ROUTES.SEND_MONEY.RECIPIENTS, { accounts })
        : history.push(ROUTES.SEND_MONEY.COUNTRY_CURRENCY_SELECTION);
    } catch (error) {
      setLoading(false);
      openErrorModal(error);
    }
  }, [history, openErrorModal, dispatch]);

  const links = useMemo(() => {
    const internalEnabled = featureFlags?.includes(FEATURE_FLAGS.SEND_MONEY_MOBILE_WALLET_ENABLE);

    return MY_CARD_TRANSFER_LINKS.map((link) => {
      if (link.id !== MOBILE_WALLET_LINK_ID) return link;

      if (internalEnabled) {
        const { modal, ...rest } = link;
        return {
          ...rest,
          featureFlag: FEATURE_FLAGS.SEND_MONEY_MOBILE_WALLET_ENABLE,
          onClick: onMobileWalletClick,
        };
      }

      return link; // SDK/modal flow
    });
  }, [featureFlags, onMobileWalletClick]);

  return { links, loading, restrictions };
};